import React, { useContext, useState, useMemo } from 'react';
import { Container, Row, Col, Button, Table, Form, Modal } from 'react-bootstrap';
import DateSelectorModal from './components/modals/modal-date-selector';
import ReportsService from '../../../../../../services/reports-service';
import { IdentityContext } from '../../../../../../context/identity-context';

const AuditConfirmation = ({ auditConfirmationReportsResponse }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [reportData, setReportData] = useState([]);
  const { user } = useContext(IdentityContext);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const reportService = new ReportsService();

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const existingTodayCount = useMemo(() => {
    const existingDates = [
      ...(auditConfirmationReportsResponse || []).map(r => r.createdDate),
      ...reportData.map(r => r.date)
    ];
    return existingDates.filter(dateStr => dateStr === today).length;
  }, [auditConfirmationReportsResponse, reportData, today]);

  const isLimitReached = existingTodayCount >= 2;

  const handlePreviewClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        report.issuerName,
        report.reportTitle,
        report.issuerRequestDate
      );
      const newWindow = window.open(
        response.fileURL,
        "_blank",
        "noopener,noreferrer"
      );
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error("Error fetching report preview:", error.message);
    }
  };

  // Function to handle report download
  const handleDownloadClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        report.issuerName,
        report.reportTitle,
        report.issuerRequestDate
      );

      const url = response.fileURL;
      const filename =
        response.fileName || url.substring(url.lastIndexOf("/") + 1);

      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error("Network response was not ok");

      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      if (error.response) {
        console.error("Error fetching report download:", error.response.data);
        console.error("Error status:", error.response.status);
        console.error("Error headers:", error.response.headers);
      } else {
        console.error("Error message:", error.message);
      }
    }
  };

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const auditConfirmationReports = auditConfirmationReportsResponse || [];

  const handleConfirmDates = (dates) => {
    setSelectedDates((prevDates) => {
      const newDates = [...new Set([...prevDates, ...dates])];
      return newDates;
    });

    const allExistingDates = [
      ...auditConfirmationReports.map((report) => report.issuerRequestDate),
      ...reportData.map((report) => report.date),
    ];

    const validDates = dates.filter(
      (date) => date && date.trim() && !allExistingDates.includes(date)
    );

    const newReports = validDates.map((date) => ({
      date,
      status: '-',
    }));

    setReportData((prevReports) => [...prevReports, ...newReports]);
    setShowModal(false);
  };

  const handleCheckboxChange = (date) => {
    setSelectedReports((prev) =>
      prev.some((selected) => selected.date === date)
        ? prev.filter((selected) => selected.date !== date)
        : [...prev, { date }]
    );
  };

  const handleRequestReports = async () => {
    try {
      const reportsToSend = selectedReports.map((report) => ({
        institutionId: user.attributes['custom:entity_id'],
        issuer: user.attributes['email'],
        issuerName: user.attributes['custom:institution_name'],
        issuerRequestDate: report.date,
        reportTitle: "Audit Confirmation",
      }));

      const response = await reportService.issuerRequestAdhocReport(reportsToSend);
      setShowSuccessModal(true);
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      console.error('Error requesting reports:', error);
      setErrorMessage(
        error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        'Unknown error'
      );
      setShowErrorModal(true);
    }
  };

  return (
    <Container className="p-4 border rounded shadow-sm report-container">
      <Row className="d-flex justify-content-between align-items-center mb-3 w-100">
        <Col>
          <h4>Audit Confirmation</h4>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handleOpenModal}
            disabled={isLimitReached}
          >
            Select Date
          </Button>
        </Col>
      </Row>

      <Table bordered hover className="mt-3">
        <thead>
          <tr>
            <th style={{ width: "1%" }}></th>
            <th>Data as of</th>
            <th>Status</th>
            <th style={{ width: "1%" }} className="text-center">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {auditConfirmationReports.length === 0 && reportData.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center">
                No reports
              </td>
            </tr>
          ) : (
            [...auditConfirmationReports, ...reportData].map((report, index) => (
              <tr key={index}>
                <td style={{ width: "1%" }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedReports.some(
                      (selected) =>
                        selected.date === (report.issuerRequestDate || report.date)
                    )}
                    onChange={() =>
                      handleCheckboxChange(report.issuerRequestDate || report.date)
                    }
                    disabled={report.status !== "-"}
                  />
                </td>
                <td>{report.issuerRequestDate || report.date}</td>
                <td>{report.status}</td>
                <td className="text-center" style={{ width: "1%" }}>
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2 mb-2"
                    onClick={() => handlePreviewClick(report)}
                    disabled={report.status !== "Report Available"}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleDownloadClick(report)}
                    disabled={report.status !== "Report Available"}
                  >
                    Download
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handleRequestReports}
            disabled={selectedReports.length === 0 || isLimitReached}
          >
            Request for Report
          </Button>
        </Col>
      </Row>

      <DateSelectorModal
        show={showModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDates}
        onDateChange={(dates) => console.log('Current Dates:', dates)}
      />

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Reports requested successfully!</Modal.Body>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
      </Modal>
    </Container>
  );
};

export default AuditConfirmation;